import React from 'react';
import ReactMarkdown from 'react-markdown';
import ExportDropdown from './ExportDropdown';
import VideoPlayer from './VideoPlayer';
import AudioPlayer from './AudioPlayer';
import TranscriptRenderer from './TranscriptRenderer';
import ChatComponent from './ChatComponent';
import type { AnalysisResult, WordTimestamp, ApiClient } from '../types';
import { findVideoSource, findAudioSource } from '../utils/sourceUtils';

interface ResultsViewProps {
  result: AnalysisResult | null;
  loading: boolean;
  loadingMessage: string;
  error: string | null;
  categoryName?: string;
  onBack: () => void;
  videoObjectUrl: string | null;
  audioObjectUrl: string | null;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  audioPlaying: boolean;
  audioCurrentTime: number;
  audioDuration: number;
  wordTimestampsState: Array<WordTimestamp> | null;
  selectedWordIndex: number | null;
  onSeekTo: (time?: number) => void;
  onSetSelectedWordIndex: (index: number | null) => void;
  onToggleAudioPlay: () => void;
  onAudioSeek: (time: number) => void;
  onSkipAudio: (seconds: number) => void;
  onFormatTime: (seconds?: number) => string;
  apiClient: ApiClient;
  updateHistoryChat?: (item: AnalysisResult, chat: Array<{ role: string; content: string }>) => void;
}

const ResultsView: React.FC<ResultsViewProps> = ({
  result,
  loading,
  loadingMessage,
  error,
  categoryName,
  onBack,
  videoObjectUrl,
  audioObjectUrl,
  videoRef,
  audioRef,
  audioPlaying,
  audioCurrentTime,
  audioDuration,
  wordTimestampsState,
  selectedWordIndex,
  onSeekTo,
  onSetSelectedWordIndex,
  onToggleAudioPlay,
  onAudioSeek,
  onSkipAudio,
  onFormatTime,
  apiClient,
  updateHistoryChat,
}) => {

  const prettify = (s?: string) => {
    if (!s) return undefined;
    return s.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const displayCategoryName = result?.categoryName || prettify(result?.category) || categoryName;

  return (
    <div className="flex-1 w-full max-w-screen-xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-start gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h2 className="text-xl font-medium">{displayCategoryName} Analysis</h2>
            {result && (
              <div className="text-sm text-text-tertiary mt-1">
                <span className="mr-4"><strong>Files Analyzed:</strong> {result.fileCount ?? 1}</span>
                <span><strong>Analyzed:</strong> {result.processingTime ? result.processingTime : '—'}</span>
              </div>
            )}
          </div>
        </div>

        {result && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const text = `${displayCategoryName} Analysis Results\n\n${result.analysis}`;
                navigator.clipboard.writeText(text);
              }}
              className="px-3 py-1.5 bg-bg-secondary hover:bg-bg-tertiary border border-border-secondary rounded-md text-sm"
            >
              Copy
            </button>

            {/* Export dropdown */}
            <div className="relative">
              <ExportDropdown result={result} />
            </div>

            <button
              onClick={() => { window.print(); }}
              className="px-3 py-1.5 bg-bg-secondary hover:bg-bg-tertiary border border-border-secondary rounded-md text-sm"
            >
              Print
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-3 p-6 bg-bg-secondary rounded-xl mb-6">
          <div className="w-6 h-6 border-2 border-brand-yellow border-t-transparent rounded-full animate-spin"></div>
          <span className="text-text-secondary">{loadingMessage}</span>
        </div>
      )}

      {error && (
        <div className="p-4 bg-semantic-error-dark/20 border border-semantic-error rounded-xl mb-6 text-semantic-error-light">
          {error}
        </div>
      )}

    {result && (result.category === 'audio_transcription' || result.category === 'video_transcription') ? (
      <div className="flex gap-6 h-[calc(100vh-180px)]">
          {/* Left half: Media and Transcript */}
          <div className="flex-1 flex flex-col gap-2 min-h-0">

            {/* Media player (video or audio) */}
            {(() => {
              const videoSrc = findVideoSource(result);
              const finalVideoSrc = videoObjectUrl || videoSrc;
              const audioSrc = findAudioSource(result);
              const finalAudioSrc = audioObjectUrl || audioSrc || videoObjectUrl;

              if (result?.category === 'video_transcription' && finalVideoSrc) {
                return <VideoPlayer src={finalVideoSrc} videoRef={videoRef} />;
              }
              if (result?.category === 'audio_transcription' && finalAudioSrc) {
                return (
                  <AudioPlayer
                    src={finalAudioSrc}
                    audioRef={audioRef}
                    audioPlaying={audioPlaying}
                    audioCurrentTime={audioCurrentTime}
                    audioDuration={audioDuration}
                    onToggleAudioPlay={onToggleAudioPlay}
                    onAudioSeek={onAudioSeek}
                    onSkipAudio={onSkipAudio}
                    formatTime={onFormatTime}
                  />
                );
              }
              return null;
            })()}

            <div className="bg-bg-secondary border border-border-secondary rounded-xl p-4 flex-1 overflow-auto prose prose-invert prose-sm min-h-0">
              <TranscriptRenderer
                result={result}
                wordTimestampsState={wordTimestampsState}
                selectedWordIndex={selectedWordIndex}
                onSetSelectedWordIndex={onSetSelectedWordIndex}
                onSeekTo={onSeekTo}
              />
            </div>
          </div>

          {/* Right half: Chat */}
          <div className="w-1/2 max-w-[520px] min-w-[320px] flex-shrink-0 flex flex-col min-h-0 h-full overflow-hidden">
            <ChatComponent result={result} apiClient={apiClient} onUpdateHistoryChat={updateHistoryChat} />
          </div>
        </div>
      ) : result && (result.category === 'medical_image_analysis' || result.category === 'scene_ai_analysis') ? (
        <div className="space-y-6">
          <div className="p-6 bg-bg-secondary rounded-xl border border-border-secondary h-[calc(100vh-180px)] overflow-y-auto">
            <div className="prose prose-invert max-w-none prose-h2:mt-6 prose-h2:first:mt-0 prose-h3:mt-4 prose-h3:first:mt-0 prose-p:mt-2 prose-ul:mt-2 prose-ol:mt-2">
              <ReactMarkdown
                components={{
                  h1: () => null, // Remove any h1 (duplicate heading)
                  h2: (props) => <><h2 className="text-lg font-semibold mb-2" {...props} /><hr className="my-4 border-white/30" /></>,
                  h3: (props) => <h3 className="text-base font-semibold mt-4 mb-2" {...props} />,
                  p: (props) => <p className="mt-2" {...props} />,
                  ul: (props) => <ul className="mt-2" {...props} />,
                  ol: (props) => <ol className="mt-2" {...props} />,
                }}
              >{result.analysis}</ReactMarkdown>
            </div>
          </div>
        </div>
      ) : result ? (
        <div className="space-y-6">
          <div className="p-6 bg-bg-secondary rounded-xl h-[calc(100vh-180px)] overflow-y-auto">
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown>{result.analysis}</ReactMarkdown>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                const text = `${displayCategoryName} Analysis Results\n\n${result.analysis}`;
                navigator.clipboard.writeText(text);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-bg-secondary hover:bg-bg-tertiary border border-border-secondary rounded-xl transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-sm">Copy</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ResultsView;
